'use client';

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const entries = new Map<string, CacheEntry<unknown>>();

const scopedKey = (userId: string, key: string) => `${userId}:${key}`;

/** In-memory, per-user cache for authenticated data. Never persisted or shared. */
export function getPrivateCache<T>(userId: string, key: string): T | null {
  const fullKey = scopedKey(userId, key);
  const entry = entries.get(fullKey);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    entries.delete(fullKey);
    return null;
  }
  return entry.value as T;
}

export function setPrivateCache<T>(userId: string, key: string, value: T, ttlMs: number): void {
  entries.set(scopedKey(userId, key), { value, expiresAt: Date.now() + ttlMs });
}

export function invalidatePrivateCache(userId: string, prefix: string): void {
  const scopedPrefix = scopedKey(userId, prefix);
  for (const key of entries.keys()) {
    if (key.startsWith(scopedPrefix)) entries.delete(key);
  }
}

/** Mutation helper for code that has authentication cookies but not the current user object. */
export function invalidateAllPrivateCaches(prefix: string): void {
  for (const key of entries.keys()) {
    const separator = key.indexOf(':');
    if (separator >= 0 && key.slice(separator + 1).startsWith(prefix)) entries.delete(key);
  }
}
